import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysCompany } from './queryKeys';

const zodAddressSchema = z.object({
  buildingNumber: z.string().nullish(),
  city: z.string().nullish(),
  country: z.string().nullish(),
  postCode: z.string().nullish(),
  streetName: z.string().nullish(),
});

const zodCompanyPublicInfoSchema = z.object({
  address: zodAddressSchema.nullish(),
  companyAccountId: z.string().uuid().nullish(),
  id: z.string().uuid(),
  name: z.string().nullish(),
});

export type CompanyPublicInfo = z.infer<typeof zodCompanyPublicInfoSchema>;

/**
 * Fetches public info (name, address) for any company by ID.
 * Requires authentication only — no company-scoped permission needed.
 * Safe to call for buyer and seller parties the user may not have full access to.
 *
 * @see https://gitlab.com/ivc-corp/hyperledger/back-end/-/merge_requests/5099
 */
export const fetchCompanyPublicInfo = async (companyId: UUID) => {
  const response = await vestigasApi
    .get(ENDPOINT.COMPANY.GET_PUBLIC_INFO(companyId))
    .json<CompanyPublicInfo>();

  return zodCompanyPublicInfoSchema.parse(response);
};

// fallow-ignore-next-line unused-export
export const getCompanyPublicInfoQueryOptions = (companyId: UUID) => ({
  enabled: Boolean(companyId),
  queryFn: () => fetchCompanyPublicInfo(companyId),
  queryKey: queryKeysCompany.getPublicInfo(companyId),
});

/**
 * React Query hook for fetching public company info by ID.
 */
export const useQueryCompanyPublicInfo = (
  companyId: UUID,
  options?: Omit<UseQueryOptions<CompanyPublicInfo>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<CompanyPublicInfo>({
    ...getCompanyPublicInfoQueryOptions(companyId),
    ...options,
  });
};
